/**
 * Système de configuration déclarative pour les entités
 * Élimine la duplication des champs, labels et configs à travers l'application
 */

import type { CSVImportConfig } from '../types/csv';
import type { FilterConfig } from '../types/filters';

// =========================================================================
// TYPES
// =========================================================================

/**
 * Définition d'un champ d'entité
 * Centralise toutes les propriétés d'un champ : label, type, visibilité, transformations, etc.
 */
export interface FieldDefinition<_T = any> {
  key: string;
  label: string;
  type: 'text' | 'date' | 'number' | 'boolean' | 'enum' | 'relation';

  // Visibilité et comportement
  showInTable?: boolean; // Défaut: true - Visible par défaut dans le tableau
  hiddenInColumnSelector?: boolean; // Défaut: false - Si true, masqué du sélecteur de colonnes (ex: id, updated_at)
  showInFilters?: boolean; // Défaut: true
  exportable?: boolean; // Défaut: true
  importable?: boolean; // Défaut: true
  sortable?: boolean; // Défaut: false
  editable?: boolean; // Défaut: false
  searchable?: boolean; // Défaut: false - Pour recherche server-side

  // Config spécifique
  required?: boolean; // Pour import CSV
  unique?: boolean; // Pour import CSV
  enumValues?: readonly string[]; // Si type = 'enum'

  // Transformations
  exportTransform?: (value: any) => string;
  importTransform?: (value: string) => any;
  importValidation?: (value: any) => boolean | string;

  // Relation
  relationResolver?: (value: string) => Promise<string | null>;

  // Affichage
  description?: string;
  placeholder?: string;
  size?: number; // Largeur colonne table
}

/**
 * Configuration complète d'une entité
 */
export interface EntityConfig<T> {
  entityName: string; // "produit"
  entityNamePlural: string; // "produits"
  fields: Record<string, FieldDefinition<T>>;
}

// =========================================================================
// HELPER
// =========================================================================

/**
 * Helper pour définir une config avec type safety
 */
export function defineEntityConfig<T>(config: EntityConfig<T>): EntityConfig<T> {
  return config;
}

// =========================================================================
// GÉNÉRATEURS
// =========================================================================

/**
 * Génère les colonnes pour CSV export
 * Retourne key (ID du champ pour matcher le transformer) et dataKey (pour accéder aux données)
 * Cela permet d'avoir plusieurs colonnes qui accèdent aux mêmes données avec des transformers différents
 * Ex: pilot_first_name et copilot_first_name accèdent tous deux à 'members' mais avec des transformers différents
 */
export function generateCSVColumns<T>(config: EntityConfig<T>) {
  return Object.entries(config.fields)
    .filter(([_, field]) => field.exportable !== false)
    .map(([fieldId, field]) => ({
      key: fieldId as keyof T, // ID du champ (pour le transformer)
      dataKey: field.key, // Clé pour accéder aux données dans l'objet
      label: field.label,
    }));
}

/**
 * Génère les transformers pour CSV export
 * Indexé par l'ID du champ (pas field.key) pour supporter plusieurs transformers sur les mêmes données
 */
export function generateCSVTransformers<T>(config: EntityConfig<T>) {
  const transformers: Record<string, (value: any) => string> = {};

  Object.entries(config.fields).forEach(([fieldId, field]) => {
    if (field.exportTransform) {
      // Indexer par l'ID du champ, pas par field.key
      transformers[fieldId] = field.exportTransform;
    } else if (field.type === 'date') {
      // Transform par défaut pour les dates
      transformers[fieldId] = (value: string) =>
        value ? new Date(value).toLocaleDateString('fr-FR') : '';
    }
  });

  return transformers;
}

/**
 * Génère la config pour CSV import
 */
export function generateImportConfig<T>(config: EntityConfig<T>): CSVImportConfig<T> {
  const importableFields = Object.entries(config.fields).filter(
    ([_, field]) => field.importable !== false
  );

  const requiredFields = importableFields
    .filter(([_, field]) => field.required === true)
    .map(([key]) => key as keyof T);

  const optionalFields = importableFields
    .filter(([_, field]) => field.required !== true)
    .map(([key]) => key as keyof T);

  const fieldLabels: Partial<Record<keyof T, string>> = {};
  const fieldDescriptions: Partial<Record<keyof T, string>> = {};
  const fieldTransforms: Partial<Record<keyof T, (value: string) => any>> = {};
  const fieldValidations: Partial<Record<keyof T, (value: any) => boolean | string>> = {};
  const relationResolvers: Partial<Record<keyof T, (value: string) => Promise<string | null>>> = {};

  importableFields.forEach(([key, field]) => {
    fieldLabels[key as keyof T] = field.label;
    if (field.description) fieldDescriptions[key as keyof T] = field.description;
    if (field.importTransform) fieldTransforms[key as keyof T] = field.importTransform;
    if (field.importValidation) fieldValidations[key as keyof T] = field.importValidation;
    if (field.relationResolver) relationResolvers[key as keyof T] = field.relationResolver;
  });

  const uniqueFields = importableFields
    .filter(([_, field]) => field.unique === true)
    .map(([key]) => key as keyof T);

  return {
    requiredFields,
    optionalFields,
    fieldLabels,
    fieldDescriptions: Object.keys(fieldDescriptions).length > 0 ? fieldDescriptions : undefined,
    fieldTransforms: Object.keys(fieldTransforms).length > 0 ? fieldTransforms : undefined,
    fieldValidations: Object.keys(fieldValidations).length > 0 ? fieldValidations : undefined,
    relationResolvers: Object.keys(relationResolvers).length > 0 ? relationResolvers : undefined,
    uniqueFields: uniqueFields.length > 0 ? uniqueFields : undefined,
  };
}

/**
 * Génère les FilterConfigs
 */
export function generateFilterConfigs<T>(config: EntityConfig<T>): FilterConfig[] {
  return Object.entries(config.fields)
    .filter(([_, field]) => field.showInFilters !== false)
    .map(([key, field]) => {
      // Déterminer le type pour FilterConfig (uniquement text, date, number, select)
      let filterType: 'text' | 'date' | 'number' | 'select';
      if (field.type === 'enum' || field.type === 'boolean') {
        filterType = 'select';
      } else if (field.type === 'relation') {
        filterType = 'text'; // Les relations sont cherchées par texte
      } else {
        filterType = field.type;
      }

      const operators =
        field.type === 'text' || field.type === 'relation'
          ? [
              'equals',
              'notEquals',
              'contains',
              'notContains',
              'startsWith',
              'notStartsWith',
              'endsWith',
              'notEndsWith',
              'isNotNull',
              'isNull',
            ]
          : field.type === 'date'
            ? [
                'equals',
                'notEquals',
                'sameDay',
                'before',
                'after',
                'between',
                'isNotNull',
                'isNull',
              ]
            : field.type === 'number'
              ? ['equals', 'notEquals', 'greaterThan', 'lessThan', 'between', 'isNotNull', 'isNull']
              : ['equals', 'notEquals', 'isNotNull', 'isNull'];

      // Générer les options pour les enums et booléens
      let options: Array<{ value: string; label: string }> | undefined;
      if (field.type === 'enum' && field.enumValues) {
        options = field.enumValues.map((value) => ({
          value,
          // Si une fonction exportTransform existe, l'utiliser pour le label
          label: field.exportTransform ? field.exportTransform(value) : value,
        }));
      } else if (field.type === 'boolean') {
        // Utiliser exportTransform si disponible pour les labels personnalisés
        options = [
          { value: 'true', label: field.exportTransform ? field.exportTransform(true) : 'Oui' },
          { value: 'false', label: field.exportTransform ? field.exportTransform(false) : 'Non' },
        ];
      }

      return {
        field: key,
        label: field.label,
        type: filterType,
        operators: operators as any,
        options,
      };
    });
}

/**
 * Génère les options de colonnes pour ColumnSelectorModal
 * Inclut toutes les colonnes sauf celles avec hiddenInColumnSelector: true
 * La visibilité par défaut est déterminée par showInTable (si non spécifié dans columnVisibility)
 * Utilise field.key pour être cohérent avec les données et l'export CSV
 */
export function generateColumnOptions<T>(
  config: EntityConfig<T>,
  columnVisibility: Record<string, boolean>
) {
  return Object.entries(config.fields)
    .filter(([_, field]) => field.hiddenInColumnSelector !== true)
    .map(([_, field]) => ({
      id: field.key,
      label: field.label,
      // Si columnVisibility a une valeur explicite, l'utiliser
      // Sinon, utiliser showInTable comme valeur par défaut (true si non spécifié)
      visible:
        columnVisibility[field.key] !== undefined
          ? columnVisibility[field.key]
          : field.showInTable !== false,
    }));
}

/**
 * Génère la liste des champs recherchables pour la recherche server-side
 */
export function generateSearchableFields<T>(config: EntityConfig<T>): string[] {
  return Object.values(config.fields)
    .filter((field) => field.searchable === true)
    .map((field) => field.key);
}

/**
 * Génère la visibilité initiale des colonnes basée sur showInTable
 * Les colonnes avec showInTable: false seront masquées par défaut
 * Utilise field.key pour être cohérent avec les données et l'export CSV
 */
export function generateInitialColumnVisibility<T>(
  config: EntityConfig<T>
): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};

  Object.entries(config.fields).forEach(([_, field]) => {
    // Les champs export-only (hiddenInColumnSelector) ne correspondent pas à de vraies colonnes
    if (field.hiddenInColumnSelector) return;
    // Si showInTable est explicitement false, masquer la colonne
    if (field.showInTable === false) {
      visibility[field.key] = false;
    }
    // Les colonnes avec showInTable: true ou undefined restent visibles (pas besoin de les ajouter)
  });

  return visibility;
}
